import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { album } from '../mock';
import { Countdown } from '../components/Countdown';
import { useNavigate } from 'react-router-dom';
import { audioManager } from '../lib/audioManager';

const HOME_AUDIO_URL = process.env.REACT_APP_HOME_AUDIO_URL;

export const HomePage = () => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [artistVisible, setArtistVisible] = useState(false);
  const artistRef = useRef(null);

  useEffect(() => {
    const audio = new Audio(HOME_AUDIO_URL);
    audio.volume = 0.5;
    audioRef.current = audio;

    let stopTimer = null;

    // Register with global audio manager so track previews can stop this
    const stopSelf = () => {
      audio.pause();
      if (stopTimer) { clearTimeout(stopTimer); stopTimer = null; }
      audioManager.clearNowPlaying(stopSelf);
    };
    audioManager.register(stopSelf);

    const startPlayback = () => {
      audioManager.requestPlayback(stopSelf, {
        title: 'Ambiance',
        subtitle: 'Brooksgordon',
        audio: audio,
      });
      audio.currentTime = 5;
      audio.play().catch(() => {});
      stopTimer = setTimeout(() => {
        audio.pause();
        audioManager.clearNowPlaying(stopSelf);
      }, 60000);
    };

    const handleLoop = () => {
      if (audio.paused) return;
      audio.currentTime = 5;
      audio.play().catch(() => {});
    };

    audio.addEventListener('ended', handleLoop);

    const handleInteraction = () => {
      if (audio.paused && !stopTimer) {
        startPlayback();
      }
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('mousemove', handleInteraction);
    };

    document.addEventListener('click', handleInteraction);
    document.addEventListener('scroll', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    document.addEventListener('touchstart', handleInteraction);
    document.addEventListener('mousemove', handleInteraction);

    // Also try auto-play immediately
    startPlayback();

    return () => {
      audioManager.unregister(stopSelf);
      if (stopTimer) clearTimeout(stopTimer);
      audio.removeEventListener('ended', handleLoop);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
      document.removeEventListener('mousemove', handleInteraction);
      audio.pause();
      audio.src = '';
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setArtistVisible(true); },
      { threshold: 0.15 }
    );
    if (artistRef.current) observer.observe(artistRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-teal-950 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-gold-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-teal-700 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8">
            <div className="space-y-4">
              <div className="flex justify-center lg:justify-start">
                <img 
                  src="https://customer-assets.emergentagent.com/job_brooksgordon-music/artifacts/z0pn85g6_inbound2351158159833860515.jpg" 
                  alt="Brooksgordon Logo" 
                  className="w-full max-w-2xl h-auto"
                />
              </div>
              <h1 className="text-2xl md:text-3xl text-gold-400 font-light tracking-wide">
                Classically Trained Vocalist &amp; Pianist
              </h1>
            </div>

            <Countdown />

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button 
                onClick={() => navigate('/store')}
                className="bg-gold-500 hover:bg-gold-600 text-black px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-gold-500/50"
              >
                Buy Now — $7.99
              </button>
              <button 
                onClick={() => navigate('/about')}
                className="bg-teal-900/50 hover:bg-teal-900/70 backdrop-blur-sm text-teal-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 border border-teal-700/50 hover:border-teal-600"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Right Content - Album Cover */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-teal-500 to-gold-500 rounded-2xl blur-2xl opacity-30 group-hover:opacity-50 transition duration-500"></div>
              <img 
                src={album.coverArt} 
                alt="The Gordon Principle Album Cover" 
                className="relative w-full max-w-md rounded-2xl shadow-2xl transform group-hover:scale-105 transition duration-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer" onClick={() => {
        const el = document.getElementById('the-artist');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }}>
        <ChevronDown size={40} className="text-teal-400" />
      </div>
    </section>

    {/* The Artist Section */}
    <section
      id="the-artist"
      ref={artistRef}
      className="relative py-24 lg:py-32 bg-gradient-to-b from-teal-950 via-gray-900 to-black overflow-hidden"
    >
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-20 right-0 w-[28rem] h-[28rem] bg-gold-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 -left-20 w-[28rem] h-[28rem] bg-teal-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Portrait */}
          <div
            className="lg:col-span-5 order-1 transition-all duration-[1200ms] ease-out"
            style={{
              opacity: artistVisible ? 1 : 0,
              transform: artistVisible ? 'translate3d(0,0,0)' : 'translate3d(-2rem,0,0)',
            }}
          >
            <div className="relative group">
              <div className="absolute -inset-3 bg-gradient-to-br from-gold-500/40 via-teal-500/30 to-transparent rounded-[2rem] blur-2xl opacity-60 group-hover:opacity-90 transition-opacity duration-700"></div>
              <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-gold-500/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
                <img
                  alt="Brooksgordon — Editorial Portrait"
                  className="w-full h-auto block transform transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                  loading="lazy"
                  src="https://customer-assets.emergentagent.com/job_d063d7a9-eda0-44cf-9fcf-715c92eeba27/artifacts/36c2ki6z_20260611_211833-IMG_STYLE.jpg"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-black/80 backdrop-blur-md border border-gold-500/40 rounded-full px-5 py-2 shadow-xl">
                <p className="text-gold-400 text-[0.7rem] uppercase tracking-[0.25em] font-semibold">Brooksgordon</p>
              </div>
            </div>
          </div>

          {/* Text content */}
          <div
            className="lg:col-span-7 order-2 space-y-7 transition-all duration-[1200ms] ease-out delay-200"
            style={{
              opacity: artistVisible ? 1 : 0,
              transform: artistVisible ? 'translateY(0)' : 'translateY(2rem)',
            }}
          >
            <div className="inline-flex items-center gap-3">
              <div className="h-px w-12 bg-gold-500"></div>
              <span className="text-gold-400 uppercase tracking-[0.3em] text-xs font-semibold">The Artist</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-50 leading-[1.05]">
              A lifetime of music,<br className="hidden md:block" />
              <span className="text-gold-400">distilled into every note.</span>
            </h2>

            <p className="text-lg md:text-xl text-teal-200 leading-relaxed max-w-2xl">
              Over three decades of classical training in piano and voice. 
              Mentored by virtuoso pianist Diane Crane and later refined under 
              vocal coach Gloria Amos at Norfolk State University. 
              Forged in Connecticut, Cuba, and the Virginia coast — from 
              dueling-piano stages to the quiet of his own studio,
              Brooksgordon has spent his life translating feeling into sound.
            </p>

            <blockquote className="border-l-2 border-gold-500/60 pl-6 italic text-teal-100/90 text-lg md:text-xl">
              "I never set out to sound like anyone. I just set out to sound honest."
            </blockquote>

            <div className="flex flex-wrap gap-4 pt-2">
              <button
                onClick={() => navigate('/about')}
                className="bg-gold-500 hover:bg-gold-600 text-black px-7 py-3.5 rounded-full font-semibold transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-gold-500/40"
              >
                Read His Story
              </button>
              <button
                onClick={() => navigate('/booking')}
                className="border border-teal-500/50 hover:border-gold-500/60 text-teal-100 hover:text-gold-300 px-7 py-3.5 rounded-full font-semibold transition-all duration-200 backdrop-blur-sm bg-black/20"
              >
                Book an Event
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* SEO Content — Atlanta Pianist & Soulful Vocalist */}
    <section className="relative py-20 lg:py-28 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-14">

          <div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-teal-50 mb-6 leading-tight">
              Atlanta Pianist &amp; Soulful Vocalist
            </h2>
            <p className="text-teal-200 text-lg leading-relaxed">
              Brooks Gordon is a classically trained pianist and soulful vocalist based in Atlanta, Georgia. 
              He performs live for weddings, private parties, corporate events, church services, intimate 
              gatherings, and special occasions. With a foundation in classical piano and a warm, expressive 
              vocal style, Brooks brings a versatile musical approach to every event. His performances can move 
              from elegant ceremony music and refined background piano to soulful vocals and contemporary 
              favorites, depending on the atmosphere you want to create. Whether you are hosting an outdoor 
              garden reception or an indoor ballroom dinner, Brooks adapts seamlessly to the space and energy 
              of your celebration.
            </p>
          </div>

          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gold-400 mb-4">
              Live Music for Weddings, Events, and Special Occasions
            </h3>
            <p className="text-teal-200 text-base md:text-lg leading-relaxed">
              Live music has a way of making an event feel personal and memorable. Brooks Gordon offers 
              flexible performance options for couples, families, businesses, and event planners throughout 
              Atlanta and the surrounding areas. For weddings, he can provide ceremony music, cocktail-hour 
              piano, dinner accompaniment, or a featured vocal performance. For private parties and corporate 
              events, he can tailor the set list to suit the mood, audience, and schedule. Whether you need 
              quiet background music or a more prominent performance, the goal is always the same: music that 
              supports the moment without overpowering it.
            </p>
          </div>

          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gold-400 mb-4">
              Classical Training Meets Soulful Expression
            </h3>
            <p className="text-teal-200 text-base md:text-lg leading-relaxed">
              Brooks's musical background combines classical discipline with a love for soul, jazz, and 
              contemporary music. That blend allows him to approach each performance with both technical care 
              and emotional warmth. Classical training helps shape his touch, timing, and attention to detail, 
              while his vocal style brings a natural, soulful quality to the songs he performs. The result is 
              a sound that can feel refined and relaxed at the same time — suitable for a formal wedding, a 
              relaxed celebration, or an intimate listening environment.
            </p>
          </div>

          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gold-400 mb-4">
              A Repertoire for the Moment
            </h3>
            <p className="text-teal-200 text-base md:text-lg leading-relaxed">
              Every event has its own personality, and the music should reflect that. Brooks works with clients 
              to understand the tone they want and can adapt his repertoire accordingly. Options may include 
              classical piano pieces, jazz standards, soul and R&amp;B favorites, popular love songs, hymns and 
              spirituals, and contemporary selections arranged for piano and voice. If you have a special song 
              in mind, he can discuss whether it can be prepared for your event. The aim is to create a set 
              list that feels personal, appropriate, and enjoyable for your guests.
            </p>
          </div>

          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gold-400 mb-4">
              Booking Brooks Gordon in Atlanta and Beyond
            </h3>
            <p className="text-teal-200 text-base md:text-lg leading-relaxed mb-6">
              Brooks Gordon is available for bookings in Atlanta, Georgia, and surrounding communities, with 
              travel considered for events beyond the metro area. He also serves the Low Country region and 
              welcomes international engagements. To inquire about availability, share your event date, venue, 
              type of occasion, and any musical preferences or special requests. Early inquiries are encouraged, 
              especially for weddings and holiday events. Whether you are planning an elegant ceremony, a 
              corporate reception, a church service, or a private celebration, Brooks would be glad to discuss 
              how live piano and vocals can add warmth and distinction to your event. For music licensing 
              inquiries related to The Gordon Principle album, please reach out directly.
            </p>
            <p className="text-teal-100 text-lg font-medium">
              Contact Brooks Gordon today to discuss your event and musical vision.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/booking')}
                className="bg-gold-500 hover:bg-gold-600 text-black px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-gold-500/40"
              >
                Book Your Event
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>
    </>
  );
};